import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useNavigate } from 'react-router';

const ColorizationBanner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper
      style={{cursor: "pointer", margin: "0 auto"}}
      onClick={() => navigate("/generate/image/simple")}
      elevation={3}
      sx={{
        width: '100%',
        maxWidth: "1000px",
        minHeight: { xs: '180px', sm: '220px' },
        background: 'linear-gradient(to right, #F8FAFC, #F1F5F9)',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          height: '100%',
          px: { xs: 1.5, sm: 3, md: 4 },
          py: { xs: 2, sm: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2, md: 3 }
        }}
      >
        {/* Text Content */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0,
          maxWidth: { xs: '60%', sm: '70%', md: 'none' }
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h2" 
            sx={{
              mb: { xs: 1, sm: 1.5 },
              fontSize: { xs: '1.125rem', sm: '1.5rem', md: '2rem' },
              lineHeight: { xs: 1.2, sm: 1.3 }
            }}
          >
            AIで
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(to right, #9333EA, #DB2777)',
                backgroundClip: 'text',
                color: 'transparent',
                whiteSpace: 'nowrap'
              }}
            >
              思い通りの色
            </Box>
            に
          </Typography>

          {/* Prompt Example */}
          <Paper
            sx={{
              bgcolor: 'grey.900',
              p: { xs: 0.75, sm: 1 },
              mb: { xs: 1, sm: 1.5 },
              maxWidth: { xs: '100%', md: 'md' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormatQuoteIcon 
                fontSize="small"
                sx={{ 
                  color: '#A78BFA',
                  fontSize: { xs: '0.875rem', sm: '1.25rem' }
                }} 
              />
              <Typography
                variant="body2"
                sx={{ 
                  fontFamily: 'monospace',
                  color: 'grey.300',
                  fontSize: { xs: '0.675rem', sm: '0.875rem' },
                  lineHeight: 1.3
                }}
              >
                夕暮れの光の中、
                <Box component="span" sx={{ color: '#FCD34D' }}>
                  黄金
                </Box>
                に輝く髪と
                <Box component="span" sx={{ color: '#F87171' }}>
                  赤
                </Box>
                ドレス
              </Typography>
            </Box>
          </Paper>

          <Typography 
            variant="body2"
            sx={{ 
              color: 'text.secondary', 
              mb: { xs: 1, sm: 1.5 },
              display: { xs: 'none', sm: 'block' },
              fontSize: { xs: '0.675rem', sm: '0.875rem' },
              lineHeight: 1.3
            }}
          >
            プロンプトで色や雰囲気を自由に
          </Typography>

          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(to right, #9333EA, #DB2777)',
              borderRadius: 50,
              textTransform: 'none',
              fontSize: { xs: '0.675rem', sm: '0.875rem' },
              py: { xs: 0.5, sm: 1 },
              px: { xs: 1.5, sm: 3 },
              minWidth: { xs: '80px', sm: 'auto' }
            }}
          >
            試す
          </Button>
        </Box>

        {/* Before & After Images */}
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1.5, md: 2 },
            flexShrink: 0
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src="/img/before.webp"
              alt="Original black and white photo"
              sx={{
                width: { xs: 60, sm: 100, md: 128 },
                height: { xs: 60, sm: 100, md: 128 },
                borderRadius: 1.5,
                filter: 'grayscale(100%)',
                objectFit: 'cover'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 2,
                left: 2,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                fontSize: { xs: '0.5rem', sm: '0.625rem', md: '0.75rem' }
              }}
            >
              Before
            </Typography>
          </Box>

          <AutoFixHighIcon 
            sx={{ 
              color: '#9333EA',
              fontSize: { xs: '0.875rem', sm: '1.25rem', md: '1.75rem' }
            }} 
          />

          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src="/img/after.webp"
              alt="Colorized photo"
              sx={{
                width: { xs: 60, sm: 100, md: 128 },
                height: { xs: 60, sm: 100, md: 128 },
                borderRadius: 1.5,
                objectFit: 'cover'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 2,
                left: 2,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                fontSize: { xs: '0.5rem', sm: '0.625rem', md: '0.75rem' }
              }}
            >
              After
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ColorizationBanner;